import { UserSessionState, SessionStorageManager, DEFAULT_SESSION_STATE } from '../types/userSession';

const SESSION_STORAGE_KEY = 'aiprep_user_session';
const SESSION_EXPIRY_DAYS = 30;

class LocalSessionStorage implements SessionStorageManager {
  private debounceTimer: NodeJS.Timeout | null = null;
  private pendingUpdates: Partial<UserSessionState> = {};
  private readonly DEBOUNCE_DELAY = 5000; // 5 seconds

  async saveSession(state: Partial<UserSessionState>): Promise<void> {
    try {
      // Get existing session from localStorage
      const existing = await this.getSession();

      // Deep merge existing session with new state
      const updatedSession = {
        ...DEFAULT_SESSION_STATE,
        ...(existing || {}),
        ...state,
        lastAccessedAt: new Date()
      };

      // Merge nested objects properly (like uiState)
      if (existing?.uiState && state.uiState) {
        updatedSession.uiState = {
          ...existing.uiState,
          ...state.uiState
        };

        // Merge nested expandedSections
        if (existing.uiState.expandedSections && state.uiState.expandedSections) {
          updatedSession.uiState.expandedSections = {
            ...existing.uiState.expandedSections,
            ...state.uiState.expandedSections
          };
        }
      }

      // Merge nested currentView
      if (existing?.currentView && state.currentView) {
        updatedSession.currentView = {
          ...existing.currentView,
          ...state.currentView
        };
      }

      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(updatedSession));
    } catch (error) {
      console.error('Error saving session to localStorage:', error);
    }
  }

  async getSession(): Promise<UserSessionState | null> {
    try {
      const stored = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!stored) return null;

      const session = JSON.parse(stored) as UserSessionState;

      // Check if session is expired
      const lastAccessed = new Date(session.lastAccessedAt);
      const daysSinceAccess = (Date.now() - lastAccessed.getTime()) / (1000 * 60 * 60 * 24);

      if (daysSinceAccess > SESSION_EXPIRY_DAYS) {
        await this.clearSession();
        return null;
      }

      return session;
    } catch (error) {
      console.error('Error reading session from localStorage:', error);
      return null;
    }
  }

  async clearSession(): Promise<void> {
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      this.pendingUpdates = {};
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
    } catch (error) {
      console.error('Error clearing session from localStorage:', error);
    }
  }

  async updateSession(updates: Partial<UserSessionState>): Promise<void> {
    // Accumulate updates with deep merge
    this.pendingUpdates = this.deepMerge(this.pendingUpdates, updates);

    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set new timer to save after debounce delay
    this.debounceTimer = setTimeout(async () => {
      await this.saveSession(this.pendingUpdates);
      this.pendingUpdates = {};
      this.debounceTimer = null;
    }, this.DEBOUNCE_DELAY);
  }

  async flush(): Promise<void> {
    // Clear the debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Save pending updates immediately if any exist
    if (Object.keys(this.pendingUpdates).length > 0) {
      await this.saveSession(this.pendingUpdates);
      this.pendingUpdates = {};
    }
  }

  private deepMerge(target: any, source: any): any {
    const output = { ...target };

    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (target[key] && typeof target[key] === 'object') {
          output[key] = this.deepMerge(target[key], source[key]);
        } else {
          output[key] = source[key];
        }
      } else {
        output[key] = source[key];
      }
    }

    return output;
  }
}

export const sessionStorage = new LocalSessionStorage();